<template>
	<div v-if="page"
		 class="outer-page">
		<main ref="innerPageRef">
			<div v-if="colorsActivated"
				 ref="coloredWrapperRef"
				 class="colored-wrapper">
				<div class="inner-page container-fluid">
					<div class="row">
						<div v-if="page.featured_image"
							 class="col-xs-12">
							<div class="featured-image-wrapper">
								<div ref="revealRef"
									 class="reveal">

									<basePicture ref="featImgRef"
												 class="featured-image"
												 :image="page.featured_image"
												 :sizes="'xs:95vw md:95vw lg:95vw xl:95vw 2xl:95vw 3xl:95vw'"
												 :width="16"
												 :height="10"
												 densities="x1"
												 loading="eager"
												 @loaded="featImgLoaded = true" />
								</div>
							</div>
						</div>
					</div>

					<div class="row work-title">
						<div ref="title"
							 class="col-xs-12 so-expand">
							<BaseDisplayTitle v-if="page.display_title"
											  :data="page.display_title"
											  tag="h1"
											  class="so-workreveal" />

							<h1 v-else
								class="so-workreveal">
								{{ page.title }}
							</h1>
						</div>
					</div>

					<div v-if="page.intro"
						 class="row work-description">
						<baseRichText class="col-xs-12 col-xxl-8 intro so-workreveal"
									  :data="page.intro" />
					</div>

					<div class="row case-info">
						<div class="col-xs-12">
							<div class="case-info-content-wrapper">
								<div class="case-categories-wrapper">
									<BaseButton v-for="item in page.categories"
												:key="item.category.id"
												:to="item.category.url"
												class="case-categories-single so-workreveal"
												classes="secondary colored"
												:magnetic="false">
										{{ item.category.title }}
									</BaseButton>
								</div>
								<div class="case-detail-wrapper">
									<div class="case-detail-client">
										<div v-if="page.client"
											 class="case-detail-entry client-name so-workreveal">
											<h6 class="case-detail-title">
												Client
											</h6>

											<span>
												{{ page.client }}
											</span>
											<!-- <br /> -->
											<!-- <span v-if="page.client_link"> — </span> -->

											<!-- <NuxtLink v-if="page.client_link"
													  :to="page.client_link"
													  class="client-link"
													  target="_blank"
													  rel="noopener">
												Website
											</NuxtLink>
											<span v-if="page.client_link_archived"
												  class="client-link"> (archived)</span> -->
										</div>

										<div v-if="page.client_link""
											 class="
											 case-detail-entry
											 client-name
											 so-workreveal">
											<h6 class="case-detail-title">
												Website
											</h6>

											<NuxtLink v-if="page.client_link"
													  :to="page.client_link"
													  class="underline"
													  target="_blank"
													  rel="noopener">
												{{ page.client_link_text || page.client_link }}
											</NuxtLink>

											<span v-if="page.client_link_archived"
												  class="client-website-link"> (archived)</span>
										</div>

										<div v-if="page.year"
											 class="case-detail-entry case-year so-workreveal">
											<h6 class="case-detail-title">
												Year
											</h6>
											<p>{{ page.year }}</p>
										</div>
									</div>
									<div v-if="page.awards"
										 class="case-detail-awards">
										<div class="case-detail-entry so-workreveal">
											<h6 class="case-detail-title">
												Awards
											</h6>

											<p v-for="award in page.awards"
											   :key="award.id">
												{{ award.award_title }}
											</p>
										</div>
									</div>
								</div>
							</div>
						</div>
					</div>
				</div>
			</div>

			<DirectusBlocks v-for="block in page?.blocks"
							:key="block.id"
							:data="block" />

			<ContentNextCase :current-case-slug="page?.slug" />

			<DirectusBlockCallToAction class="cta-bottom" />
		</main>
	</div>
</template>

<script setup>
const { directus, readItems } = useDirectus()
const { gsap, ScrollTrigger } = useGsap()
const { $bus } = useNuxtApp()
const metaStore = useMetaStore()
const config = useRuntimeConfig()
const route = useRoute()
const slug = computed(() => route.params.workslug)
const previewStatus = computed(() => route.query?.status || 'published')
const url = route.fullPath

const coloredWrapperRef = ref(null)
const innerPageRef = ref(null)
const featImgRef = ref(null)
const revealRef = ref(null)

const colorsActivated = ref(false)
const featImgLoaded = ref(false)
const triggers = {}
let $q, revealFeaturedTL

const { data: pageData } = await useAsyncData(`${url}`, () => {
	return directus.request(readItems('work', singleWorkQuery(slug.value, previewStatus.value)))
})

const page = computed(() => {
	if (!pageData.value?.length) showError({ statusCode: 404, statusMessage: 'Page Not Found' })

	return pageData?.value[0]
})

useSuperSeoMeta(page.value, { ...config, url })

function animRevealFeaturedImage() {
	revealFeaturedTL = gsap.timeline({
		paused: true,
	})

	revealFeaturedTL.set(revealRef.value, { autoAlpha: 1 })
	revealFeaturedTL
		.fromTo(revealRef.value, {
			xPercent: -100,
		}, {
			autoAlpha: 1,
			xPercent: 0,
			duration: 1.2,
			ease: 'power4.inOut'
		})
		.fromTo(featImgRef.value.$el, {
			xPercent: 100,
			scale: 1.4,
		}, {
			xPercent: 0,
			scale: 1,
			duration: 1.2,
			ease: 'power4.inOut'
		}, '<')

	revealFeaturedTL.play()
}

function animSetHeaderColor() {
	triggers.changeWorkHeader = ScrollTrigger.create({
		id: 'changeWorkHeader',
		trigger: coloredWrapperRef.value,
		end: 'bottom top+=75',
		onEnter: () => {
			$bus.$emit('header-change-color', { primary: metaStore.activePageColors.foregroundColor, secondary: metaStore.activePageColors.backgroundColor })
		},
		onLeave: () => {
			$bus.$emit('header-change-color', { primary: '#121212', secondary: '#fdfdfd' })
		},
		onEnterBack: () => {
			$bus.$emit('header-change-color', { primary: metaStore.activePageColors.foregroundColor, secondary: metaStore.activePageColors.backgroundColor })
		}
	})
}

function checkImgLoaded() {
	if (featImgLoaded.value === false) {
		delay(10).then(() => {
			const image = document.querySelector('.featured-image img')
			let isLoaded = false
			isLoaded = image.complete && image.naturalHeight !== 0

			if (isLoaded) {
				// console.log('Workpage: inner image check', isLoaded)
				featImgLoaded.value = true

				delay(10).then(() => {
					animRevealFeaturedImage()
				})
			}
			else {
				checkImgLoaded()
			}
		})
	} else {
		// console.log('Workpage: image loaded before outer check')
		delay(10).then(() => {
			animRevealFeaturedImage()
		})

	}
}

function staggerReveal() {
	const elements = $q('.so-workreveal')

	elements.forEach((el, i) => {
		triggers['staggerReveal' + i] = gsap.from(el, {
			autoAlpha: 0,
			yPercent: 30,
			duration: 1.2,
			// stagger: 0.15,
			delay: () => 1 + (metaStore.getDelay / 1000) + (i * 0.12),
			ease: 'expo.out'
		})
	})
}

function initAnims() {
	$q = gsap.utils.selector(coloredWrapperRef.value)

	animSetHeaderColor()
	staggerReveal()

	checkImgLoaded()

	$bus.$emit('page-mounted')
}


onMounted(() => {
	metaStore.setActivePageColors(page.value)
	colorsActivated.value = true

	useAnims(initAnims, triggers)
	useCssAnims(innerPageRef.value)
})
</script>
<style lang="scss" scoped>
:deep(.intro) {
	a {
		@include standard-link;
	}
}

.so-workreveal {
	visibility: hidden;
}

.inner-page {
	@include bigger-padding-top;
}

.colored-wrapper {
	background-color: var(--background-color, $white);
	transition: background-color 0.5s $easeOutQuint;
	color: var(--foreground-color, $black);
	margin-bottom: -30px;
	@include smaller-padding-bottom;
	min-height: 50vh;

	*::selection {
		background-color: var(--foreground-color, $white);
		color: var(--background-color, $black);
	}

	.work-title {
		margin-top: rem-calc(24);

		@include for-devices-above(sm) {
			margin-top: rem-calc(30);
		}

		@include for-devices-above(md) {
			margin-top: rem-calc(40);
		}

		@include for-devices-above(lg) {
			margin-top: rem-calc(48);
		}

		h1 {
			margin: 0;
			visibility: none;
		}
	}

	.work-description,
	.case-info {
		margin-top: rem-calc(24);

		@include for-devices-above(sm) {
			margin-top: rem-calc(36);
		}

		@include for-devices-above(md) {
			margin-top: rem-calc(48);
		}

		@include for-devices-above(lg) {
			margin-top: rem-calc(56);
		}
	}

	.work-description {
		:deep(.richtext) {
			p {
				&:first-of-type {
					margin-top: 0;
				}

				&:last-of-type {
					margin-bottom: 0;
				}
			}
		}

	}

	.case-info {
		@include for-devices-above(lg) {
			margin-top: rem-calc(100);
		}

		.case-info-content-wrapper {
			display: flex;
			align-items: flex-start;
			flex-flow: column wrap;
			gap: calc(#{$outer-margin} / 3);

			@include for-devices-above(md) {
				gap:calc(#{$outer-margin} / 2);
			}

			@include for-devices-above(lg) {
				flex-flow: row nowrap;
				gap: calc($gutter-width);
			}

			.case-categories-wrapper {
				display: flex;
				flex-flow: row wrap;
				flex-basis: 100%;
				gap: calc(#{$outer-margin} / 3);

				@include for-devices-above(sm) {
					flex-basis: 50%;
				}

				@include for-devices-above(md) {
					gap:calc(#{$outer-margin} / 2);
				}

				@include for-devices-above(lg) {
					gap: calc($gutter-width);
					flex-basis: 66.66666667%;
				}
			}

			.case-detail-wrapper {
				display: flex;
				flex-flow: column nowrap;
				justify-content: flex-start;
				flex-basis: calc(100% - ($outer-margin / 3));
				gap:calc(#{$outer-margin} / 2);
				width: 100%;

				@include for-devices-above(sm) {
					flex-flow: row nowrap;
				}

				@include for-devices-above(lg) {
					flex-basis: 33.33333333%;
					flex-flow: row wrap;
					gap: calc($gutter-width) * 2.5;
				}

				.case-detail-client {
					display: flex;
					flex-flow: row nowrap;
					flex-basis: 100%;
					gap: calc(#{$outer-margin} / 3);

					@include for-devices-above(md) {
						gap:calc(#{$outer-margin} / 2);
						flex-basis: 100%;
					}

					@include for-devices-above(lg) {
						gap: calc($gutter-width);
						flex-basis: 100%;
					}

					.client-link {
						@include textBodySmall;
					}

					// .client-link {
					// 	@include textBodySmall;

					// 	a {
					// 		box-shadow: 0 2px 0 0;

					// 		&:hover {
					// 			box-shadow: 0 4px 0 0;
					// 			transition: box-shadow 0.2s ease-in-out;
					// 			transition-property: box-shadow;
					// 			color: var(--cursor-background-color);
					// 		}
					// 	}
					// }
				}

				.case-detail-entry {
					word-wrap: break-word;
					@include textBody;

					&.client-name {
						margin-top: 0;
						flex-grow: 3;

						.case-detail-title {
							@include font-bold;
						}
					}

					&.case-year {
						flex-grow: 1;

					}

					a {
						box-shadow: 0 2px 0px -0px var(--foreground-color);
						transition-property: box-shadow;
						transition: 0.2s ease-in-out;

						&:hover {
							box-shadow: 0 4px 0px -0px var(--background-color);
						}
					}
				}

				.case-detail-awards {
					display: flex;
					flex-flow: row wrap;
					flex-basis: 100%;
				}
			}
		}
	}
}

.flex-module {
	&:first-of-type {
		margin-top: 0;
	}

	&:last-of-type {
		margin-bottom: 0;
	}

	@include smaller-top-bottom-margin;
}

.featured-image-wrapper {
	position: relative;
	overflow: hidden;
	@include rounded-corners;

	&::before {
		content: '';
		display: block;
		width: 100%;
		height: 100%;
		position: absolute;
		top: 0;
		left: 0;
		background-color: var(--background-color, $white);
		opacity: 0.25;
		mix-blend-mode: difference;
	}

	.reveal {
		overflow: hidden;
		position: relative;
		visibility: hidden;
		aspect-ratio: 16 / 9;
		@include rounded-corners;

		:deep(.featured-image) {
			min-width: 100%;
			max-height: 100%;
			height: auto;
			display: block;
			position: relative;
			margin: 0 auto;


			img {
				display: block;
				width: 100%;
				object-fit: cover;
			}
		}
	}
}
</style>